import {
  faArrowDownToSquare,
  faArrowUpFromSquare,
  faFileCircleMinus,
  faFileCirclePlus,
  faFiles,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup, Toolbar } from "@progress/kendo-react-buttons";
import { cloneArray } from "@progress/kendo-react-common";
import { Splitter } from "@progress/kendo-react-layout";
import {
  fieldFocusIndex,
  selectedSectionName,
  selectedFieldName as selectedTemplateFieldName,
  templateData,
} from "../../Template/NewTemplateSignals";
import { GalleryConstructorGrid } from "./GalleryConstructorGrid";
import { GalleryConstructorPropertyPane } from "./GalleryConstructorPropertyPane";
import {
  editedPropertyValues,
  galSessionData,
  galleryConstructorGridRows,
  galleryConstructorUniqueFieldNames,
  galleryConstructorValidationErrors,
  galleryConstructorVisibility,
  gridPaneSize,
  maxId,
  nestedPanes,
  selectedFieldIndex,
  selectedFieldName,
  selectedFieldType,
} from "./GalleryConstructorSignals";

const handlePropertyChange = ({ propName, propValue }) => {
  if (fieldFocusIndex.value === null) {
    templateData.value = {
      ...templateData.value,
      sections: templateData.value.sections.map((section) => {
        if (section.sectionName === selectedSectionName.value) {
          return {
            ...section,
            properties: {
              ...section.properties,
              [propName]: propValue,
            },
          };
        }
        return section;
      }),
    };
  } else {
    templateData.value = {
      ...templateData.value,
      sections: templateData.value.sections.map((section) => {
        if (section.sectionName === selectedSectionName.value) {
          return {
            ...section,
            fields: section.fields.map((field) => {
              if (field.fieldName === selectedTemplateFieldName.value) {
                return {
                  ...field,
                  properties: {
                    ...field.properties,
                    [propName]: propValue,
                  },
                };
              }
              return field;
            }),
          };
        }
        return section;
      }),
    };
  }
};

const GalleryConstructor = ({ properties, currentPropertyValues }) => {
  if (!currentPropertyValues) {
    return;
  }

  if (galSessionData.value.length === 0) {
    galSessionData.value = properties.map((property, index) => {
      return { id: index, ...property };
    });
  }

  //#region UI Interactions
  const handleSubmit = () => {
    const updatedGridRows = galSessionData.value.map((row) => {
      if (editedPropertyValues.value[row.fieldName]) {
        return {
          ...row,
          properties: {
            ...row.properties,
            ...editedPropertyValues.value[row.fieldName],
          },
        };
      }
      return row;
    });

    editedPropertyValues.value = {};

    handlePropertyChange({ propName: "Fields", propValue: updatedGridRows });
    galleryConstructorGridRows.value = updatedGridRows;
    galleryConstructorVisibility.value = !galleryConstructorVisibility.value;
  };

  const handleNewField = () => {
    const newField = {
      id: maxId + 1,
      fieldName: null,
      fieldType: "",
      properties: {},
    };
    const updatedRows = cloneArray(galSessionData.value);
    updatedRows.map((row) => {
      return { ...row, properties: editedPropertyValues.value[row.fieldName] };
    });
    selectedFieldName.value = newField.fieldName;
    selectedFieldType.value = newField.fieldType;
    selectedFieldIndex.value = newField.id;
    galSessionData.value = [...updatedRows, newField];
  };

  const handleDuplicateField = () => {
    const selectedField = galSessionData.value[selectedFieldIndex.value];
    const newField = {
      id: maxId + 1,
      fieldName: selectedField.fieldName,
      fieldType: selectedField.fieldType,
      properties: selectedField.properties,
    };
    selectedFieldName.value = newField.fieldName ?? "";
    selectedFieldType.value = newField.fieldType ?? "";
    selectedFieldIndex.value = newField.id;
    galSessionData.value = [...galSessionData.value, newField];
  };

  const handleDeleteField = () => {
    const newRows = [...galSessionData.value];
    const selectedRow = newRows[selectedFieldIndex.value];
    const index = newRows.findIndex((item) => item.id === selectedRow.id);
    newRows.splice(index, 1);

    galSessionData.value = newRows;
  };

  const handleMoveUp = () => {
    const newRows = [...galSessionData.value];

    const selectedRow = newRows[selectedFieldIndex.value];
    const index = newRows.findIndex((item) => item.id === selectedRow.id);

    if (index > 0) {
      // Swap positions
      const tempRule = newRows[index - 1];
      newRows[index - 1] = newRows[index];
      newRows[index] = tempRule;

      // Swap id values
      const tempId = newRows[index - 1].id;
      newRows[index - 1].id = newRows[index].id;
      newRows[index].id = tempId;

      // Swap selected values
      newRows[index - 1].selected = true;
      newRows[index].selected = false;

      selectedFieldIndex.value = index - 1;
    }
    galSessionData.value = [...newRows];
  };

  const handleMoveDown = () => {
    const newRows = [...galSessionData.value];

    const selectedRow = newRows[selectedFieldIndex.value];
    const index = newRows.findIndex((item) => item.id === selectedRow.id);

    if (index < newRows.length - 1) {
      // Swap positions
      const tempRule = newRows[index + 1];
      newRows[index + 1] = newRows[index];
      newRows[index] = tempRule;

      // Swap id values
      const tempId = newRows[index + 1].id;
      newRows[index + 1].id = newRows[index].id;
      newRows[index].id = tempId;

      // Swap selected values
      newRows[index + 1].selected = true;
      newRows[index].selected = false;

      selectedFieldIndex.value = index + 1;
    }

    galSessionData.value = [...newRows];
  };
  //#endregion UI Interactions

  return (
    <div className="condition-rule-page-content">
      <div className="gallery-constructor-container">
        <Toolbar>
          <ButtonGroup>
            <Button onClick={handleNewField}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFileCirclePlus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                New Field
              </div>
            </Button>
            <Button onClick={() => handleDeleteField()}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  className="toolbar-button-icon"
                  icon={faFileCircleMinus}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Delete Field
              </div>
            </Button>
            <Button onClick={handleDuplicateField}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faFiles}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
                Duplicate Field
              </div>
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button onClick={handleMoveUp}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowUpFromSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
            <Button onClick={handleMoveDown}>
              <div
                className="align-columns"
                style={{ margin: 0 }}
              >
                <FontAwesomeIcon
                  icon={faArrowDownToSquare}
                  style={{
                    "--fa-primary-color": "#0078D4",
                    "--fa-secondary-color": "#919191",
                    "--fa-primary-opacity": "1",
                    "--fa-secondary-opacity": "1",
                    fontSize: 20,
                  }}
                />
              </div>
            </Button>
          </ButtonGroup>
          <div></div>
        </Toolbar>

        <Splitter
          style={{
            height: "90%",
            maxHeight: "90%",
            borderBottomRightRadius: "20px",
            borderBottomLeftRadius: "20px",
            overflowY: "auto",
          }}
          orientation="horizontal"
          panes={nestedPanes.value}
          onChange={(e) => (gridPaneSize.value = e.newState[0].size)}
        >
          <GalleryConstructorGrid />
          <GalleryConstructorPropertyPane />
        </Splitter>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          onClick={() => handleSubmit()}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          disabled={
            galleryConstructorValidationErrors.value.length > 0 ||
            galleryConstructorUniqueFieldNames.value.length > 0
          }
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={() => {
            galleryConstructorVisibility.value = !galleryConstructorVisibility.value;
            galSessionData.value = [];
          }}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default GalleryConstructor;
